import { useBridgeUser } from '@sendible/shared-state-bridge';
import { AnimatePresence } from 'framer-motion';
import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { EVENTS, subscribe } from '@sendible/event-manager';
import { DndContext, DragEndEvent, DragOverlay, DragStartEvent, PointerSensor, useSensor, useSensors } from '@dnd-kit/core';
import { DateTime, FixedOffsetZone } from 'luxon';
import { BulkActionsBar } from '@sendible/design-system';
import { useRescheduleMessage } from '@sendible/frontendv2/src/models/calendar';
import { CalendarGrid } from './components/CalendarGrid';
import { Container } from './index.styles';
import { ConfigBar, DeleteMultipleMessagesModal, FilterBar, SidebarModal, TabNavBar } from './components';
import { useCalendarContext, useCalendarSidebarContext } from './context';
import { useSelectionContext } from './selectionContext';
import { CalendarCard } from './components/CalendarCard';
import { canDragMessage, computeRescheduledSendDate, getCalendarCardMedias, hasCalendarEndpointQueryKey } from './utils';
import queryClient from '../../data-layer/queryClient';
import endpoints from '../../data-layer/endpoints';
import { PrintReset } from '../../components';

export const CalendarPage = () => {
  const user = useBridgeUser();
  const userWeekStartDay = user?.weekStart;
  const { viewMode, setUserWeekStartDay, density, closeSidebar } = useCalendarContext();
  const { isSidebarOpen } = useCalendarSidebarContext();
  const { deselectAll, isSelectAllActive, isSelectingAll, maxItemsCount, selectAll, selectedMessageIds } = useSelectionContext();
  const { t } = useTranslation('calendar');
  const [activeMessage, setActiveMessage] = useState<CalendarMessage | null>(null);
  const [isBulkDeleteConfirmOpen, setIsBulkDeleteConfirmOpen] = useState(false);
  const [isMediaPreviewOpen, setIsMediaPreviewOpen] = useState(false);
  const dragFromSidebarRef = useRef(false);
  const updateMessage = useRescheduleMessage();
  const isWorkspaceMode = Boolean(user?.isWorkspaceMode);

  const hideSidebarLayer = () => {
    const layerRoot = document.getElementById('layer-root');

    if (layerRoot) {
      layerRoot.style.transition = 'opacity 0.15s ease-out';
      layerRoot.style.opacity = '0';
      layerRoot.style.pointerEvents = 'none';
    }
  };

  const restoreSidebarLayer = () => {
    const layerRoot = document.getElementById('layer-root');

    if (layerRoot) {
      layerRoot.style.transition = '';
      layerRoot.style.opacity = '';
      layerRoot.style.pointerEvents = '';
    }
  };

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: { distance: 4 },
    })
  );

  const handleDragStart = (event: DragStartEvent) => {
    const payload = event.active.data.current as { message: CalendarMessage } | undefined;

    if (payload) {
      setActiveMessage(payload.message);

      if (isSidebarOpen) {
        dragFromSidebarRef.current = true;
        hideSidebarLayer();
      }
    }
  };

  const handleDragEnd = ({ active, over }: DragEndEvent) => {
    const wasDraggingFromSidebar = dragFromSidebarRef.current;

    dragFromSidebarRef.current = false;

    if (!over) {
      if (wasDraggingFromSidebar) restoreSidebarLayer();
      setActiveMessage(null);

      return;
    }

    const payload = active.data.current as { message: CalendarMessage; fromDateKey: string } | undefined;

    if (!payload) {
      if (wasDraggingFromSidebar) restoreSidebarLayer();
      setActiveMessage(null);

      return;
    }

    const { message, fromDateKey } = payload;
    const targetDateKey = String(over.id);

    if (fromDateKey !== targetDateKey) {
      if (!canDragMessage(message)) {
        if (wasDraggingFromSidebar) restoreSidebarLayer();
        setActiveMessage(null);

        return;
      }

      const zone = FixedOffsetZone.instance(user.timezone);
      const userNow = DateTime.now().setZone(zone);
      const todayStr = userNow.toISODate() ?? new Date().toISOString().slice(0, 10);

      if (targetDateKey < todayStr) {
        if (wasDraggingFromSidebar) restoreSidebarLayer();
        setActiveMessage(null);

        return;
      }

      const messageTime = message.sendDateLocalized.slice(11) || '00:00:00';

      const newSendDate = computeRescheduledSendDate({
        newDate: targetDateKey,
        messageTime,
        timezoneOffsetMinutes: user.timezone,
        now: userNow,
      });

      updateMessage.mutate({
        message,
        newSendDate,
        recursUntil: undefined,
      });

      if (wasDraggingFromSidebar) {
        closeSidebar();
        /* 🛠️ Upgraded for v12: Ensure layers are restored immediately 
           alongside sidebar context updates to avoid unmount cutoff skips */
        restoreSidebarLayer();
      }
    } else if (wasDraggingFromSidebar) {
      restoreSidebarLayer();
    }

    setActiveMessage(null);
  };

  const handleDragCancel = () => {
    if (dragFromSidebarRef.current) {
      dragFromSidebarRef.current = false;
      restoreSidebarLayer();
    }
    setActiveMessage(null);
  };

  const overlayCardType = viewMode === 'monthly' ? (density === 'comfortable' ? 'monthly' : 'compact') : density === 'compact' ? 'compact' : 'weekly';

  useEffect(() => {
    setUserWeekStartDay(userWeekStartDay ? 'monday' : 'sunday');
  }, [userWeekStartDay]);

  useEffect(() => {
    const cleanQueries = () => {
      const invalidateCalendarQueries = (endpoint: string) =>
        queryClient.invalidateQueries({
          predicate: ({ queryKey }) => hasCalendarEndpointQueryKey(queryKey, endpoint),
        });

      invalidateCalendarQueries(endpoints.GetCalendar.endpoint);
      invalidateCalendarQueries(endpoints.GetCalendarSummary.endpoint);
      invalidateCalendarQueries(endpoints.GetCalendarMessage.endpoint);
    };

    const unsubscribeComposeMessageSaved = subscribe(EVENTS.COMPOSE_SAVE_COMPLETE, () => cleanQueries());
    const unsubscribeComposeMessageDeleted = subscribe(EVENTS.COMPOSE_DELETE_COMPLETE, () => cleanQueries());
    const unsubscribeTimezoneChanged = subscribe(EVENTS.TIMEZONE_CHANGED, () => cleanQueries());
    const unsubscribeUserChanged = subscribe(EVENTS.SWITCH_USER, () => cleanQueries());

    return () => {
      unsubscribeComposeMessageSaved();
      unsubscribeComposeMessageDeleted();
      unsubscribeTimezoneChanged();
      unsubscribeUserChanged();
    };
  }, []);

  // The floating bulk-actions bar and the media lightbox both render into fixed-position
  // portals with overlapping z-indexes (#modal-root sits above #lightbox-root) — hide the bar
  // while a preview is open so it doesn't sit on top of the lightbox's own controls.
  useEffect(() => {
    const unsubscribe = subscribe(EVENTS.MEDIA_PREVIEW_TOGGLE, (data?: { isOpen?: boolean }) => {
      setIsMediaPreviewOpen(Boolean(data?.isOpen));
    });

    return () => {
      unsubscribe();
    };
  }, []);

  return (
    <>
      <PrintReset />
      <DndContext
        sensors={sensors}
        onDragStart={handleDragStart}
        onDragEnd={handleDragEnd}
        onDragCancel={handleDragCancel}
      >
        <Container data-calendar-root>
          {/* 🛠️ Upgraded for v12: Mode assignment is clean, and state adjustments
             are triggered directly via state/callbacks rather than trailing animation hooks */}
          <AnimatePresence mode="wait">{isSidebarOpen && <SidebarModal />}</AnimatePresence>
          <TabNavBar />
          <FilterBar />
          <ConfigBar />
          <CalendarGrid view={viewMode} />
        </Container>
        {createPortal(
          <AnimatePresence>
            {selectedMessageIds.length > 0 && !isMediaPreviewOpen && (
              <BulkActionsBar
                key="calendar-bulk-actions-bar"
                appearance="floating"
                actionButtons={[{ icon: 'trash', label: t('delete') as string, action: 'delete' }]}
                act={(action) => action === 'delete' && setIsBulkDeleteConfirmOpen(true)}
                deselectAll={deselectAll}
                isAllSelected={isSelectAllActive}
                isSelectAllLoading={isSelectingAll}
                maxItemsCount={maxItemsCount}
                selectAll={selectAll}
                selectedItemsCount={selectedMessageIds.length}
                selectedItemsLabel={t('bulk_actions_selected') as string}
                testId="calendar-bulk-actions-bar"
              />
            )}
          </AnimatePresence>,
          document.getElementById('modal-root') as HTMLElement
        )}
        {isBulkDeleteConfirmOpen &&
          createPortal(
            <DeleteMultipleMessagesModal close={() => setIsBulkDeleteConfirmOpen(false)} />,
            document.getElementById('modal-root') as HTMLElement
          )}
        <DragOverlay dropAnimation={null}>
          {activeMessage && (
            <div style={{ pointerEvents: 'none' }}>
              <CalendarCard
                id={Number(activeMessage.id)}
                isDragging
                onClick={() => null}
                deliveryStatus={activeMessage.deliveryStatus}
                postAt={activeMessage.sendDateLocalized}
                destinations={activeMessage.destinations || []}
                status={activeMessage.status}
                medias={getCalendarCardMedias(activeMessage.body)}
                recurrence={activeMessage.recurrence}
                title={activeMessage.body?.messageText || activeMessage.subject}
                type={overlayCardType}
                workspace={isWorkspaceMode ? activeMessage.author || activeMessage.workspace : activeMessage.workspace}
              />
            </div>
          )}
        </DragOverlay>
      </DndContext>
    </>
  );
};
